import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { apiClient } from "@/api/ApiClient";
import type { AuthSessionResponse } from "@/auth/authTypes";
import {
  isSocialProviderAvailable,
  signInWithProvider,
  type SocialProvider,
} from "@/lib/socialLogin";

interface SocialLoginButtonsProps {
  /**
   * Called with the backend session. Signed out it is a sign-in (or a new
   * account); signed in, the provider account is linked to the current one.
   */
  onSession: (session: AuthSessionResponse) => void | Promise<void>;
  disabled?: boolean;
}

/** True when at least one native provider can be offered on this device. */
export const hasSocialLogin = () =>
  isSocialProviderAvailable("google") || isSocialProviderAvailable("apple");

const GoogleMark = () => (
  <svg viewBox="0 0 48 48" className="h-4 w-4 mr-2" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.5 13.6 17.8 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9h12.4c-.5 2.9-2.2 5.3-4.6 6.9l7.4 5.7c4.3-4 6.9-9.9 6.9-17z" />
    <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.6 0 20.2 0 24s1 7.4 2.7 10.7l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.4-5.7c-2.1 1.4-4.8 2.2-8.5 2.2-6.2 0-11.5-4.1-13.4-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z" />
  </svg>
);

const AppleMark = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2" aria-hidden="true" fill="currentColor">
    <path d="M16.37 12.64c-.02-2.13 1.74-3.15 1.82-3.2-.99-1.45-2.54-1.65-3.09-1.67-1.31-.13-2.57.78-3.23.78-.67 0-1.7-.76-2.79-.74-1.43.02-2.76.84-3.5 2.12-1.5 2.6-.38 6.44 1.07 8.55.71 1.03 1.55 2.18 2.66 2.14 1.07-.04 1.47-.69 2.76-.69 1.29 0 1.65.69 2.78.67 1.15-.02 1.88-1.05 2.58-2.08.82-1.19 1.15-2.35 1.17-2.41-.03-.01-2.24-.86-2.26-3.4zM14.25 6.39c.59-.72.99-1.71.88-2.7-.85.04-1.88.57-2.49 1.28-.55.63-1.03 1.64-.9 2.61.95.07 1.92-.48 2.51-1.19z" />
  </svg>
);

export const SocialLoginButtons = ({ onSession, disabled }: SocialLoginButtonsProps) => {
  const { t, language } = useTranslation();
  const { toast } = useToast();
  const [busy, setBusy] = useState<SocialProvider | null>(null);

  const handle = async (provider: SocialProvider) => {
    if (busy) return;
    setBusy(provider);
    try {
      const credential = await signInWithProvider(provider);
      if (!credential) return; // sheet closed
      const session = (await apiClient.post("/auth/social", {
        ...credential,
        preferredLanguage: language,
      })) as AuthSessionResponse;
      if (!session?.accessToken) throw new Error("Invalid sign-in response");
      await onSession(session);
    } catch (err) {
      console.error("[SocialLogin] sign-in failed", err);
      const status = (err as { status?: number })?.status;
      toast({
        title: t("common.error"),
        description: status === 409 ? t("auth.social_in_use") : t("auth.social_failed"),
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-2">
      {isSocialProviderAvailable("apple") && (
        <Button
          type="button"
          className="w-full bg-black text-white hover:bg-black/90 border border-white/20"
          disabled={disabled || busy !== null}
          onClick={() => void handle("apple")}
        >
          {busy === "apple" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <AppleMark />}
          {t("auth.continue_with_apple")}
        </Button>
      )}
      {isSocialProviderAvailable("google") && (
        <Button
          type="button"
          variant="outline"
          className="w-full bg-white text-black hover:bg-white/90 border-primary/20"
          disabled={disabled || busy !== null}
          onClick={() => void handle("google")}
        >
          {busy === "google" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GoogleMark />}
          {t("auth.continue_with_google")}
        </Button>
      )}
    </div>
  );
};
